import React, { ReactElement, useCallback, useRef } from 'react';
import { FeedbackButtonType, QuickFeedback, ThumbType } from '@atlassian/learning-components';
import { useLoaderData } from 'react-router-dom';

import { Opinion, submitQuickFeedback } from 'src/services/http/quickFeedback';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { sendUIEvent } from 'src/utils/analytics';
import { LessonLoaderProps } from 'src/pages/LessonPage';
import { getSlug } from 'src/utils/slug';

interface QuickFeedbackComponentProps {
  shouldShowDivider?: boolean;
}

export const QuickFeedbackComponent = ({ shouldShowDivider }: QuickFeedbackComponentProps): ReactElement => {
  const { course, learningPath, lesson } = useLoaderData() as LessonLoaderProps;

  const courseSlug = getSlug(course);
  const lessonSlug = getSlug(lesson);
  const learningPathSlug = getSlug(learningPath);
  const { user } = useAtlassianUser();

  const feedback = useRef({
    opinion: '',
    thoughts: '',
    selectedOption: '',
  });

  const handleChangeThoughts = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    feedback.current.thoughts = e.target.value;
  }, []);

  const handleChangeOption = useCallback((option: string) => {
    feedback.current.selectedOption = option;
  }, []);

  const handleThumbClick = useCallback((thumbType: ThumbType) => {
    feedback.current.opinion = thumbType === ThumbType.THUMB_UP ? Opinion.SATISFIED : Opinion.NOT_SATISFIED;
  }, []);

  const handleSubmit = useCallback(
    (buttonType: FeedbackButtonType) => {
      submitQuickFeedback({
        opinion: feedback.current.opinion as Opinion,
        thoughts: feedback.current.thoughts,
        selectedOption: feedback.current.selectedOption,
        lessonSlug: lessonSlug,
        courseSlug: courseSlug,
        learningPathSlug: learningPathSlug,
      });

      sendUIEvent(
        'lessonScreen',
        'button',
        'clicked',
        buttonType === FeedbackButtonType.SUBMIT ? 'submitFeedbackButton' : 'dismissFeedbackButton',
        {
          thumbType: feedback.current.opinion === Opinion.SATISFIED ? ThumbType.THUMB_UP : ThumbType.THUMB_DOWN,
          isThoughtsFieldEmpty: !feedback.current.thoughts.length,
          thoughts: feedback.current.thoughts,
          selectedOption: feedback.current.selectedOption,
          course: course?.title || '',
          learningPath: learningPath?.title || '',
        },
        user?.account_id,
      );
    },
    [courseSlug, lessonSlug, course?.title, learningPathSlug, learningPath?.title, user?.account_id],
  );

  return (
    <QuickFeedback
      onSubmitFeedback={handleSubmit}
      onChangeThoughts={handleChangeThoughts}
      onChangeOption={handleChangeOption}
      onThumbClick={handleThumbClick}
      shouldShowDivider={shouldShowDivider}
    />
  );
};
